import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { bootstrapSession } from '../api/client';
import { selectIsAuthenticated, useAuthStore } from './authStore';

interface Props {
  children: ReactNode;
}

// One silent-refresh attempt per page load. Module-level so navigating
// between protected routes doesn't re-trigger it after a failure.
let bootstrapAttempted = false;

export function ProtectedRoute({ children }: Props) {
  const authenticated = useAuthStore(selectIsAuthenticated);
  const location = useLocation();
  const [restoring, setRestoring] = useState(!authenticated && !bootstrapAttempted);

  useEffect(() => {
    if (authenticated || bootstrapAttempted) {
      return;
    }
    bootstrapAttempted = true;
    let cancelled = false;
    void bootstrapSession().finally(() => {
      if (!cancelled) {
        setRestoring(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  if (authenticated) {
    return <>{children}</>;
  }
  if (restoring) {
    return (
      <Center h="100vh">
        <Loader color="wheelsRed" />
      </Center>
    );
  }
  return <Navigate to="/login" replace state={{ from: location.pathname }} />;
}
